import type { FeedbackType } from '@/modules/perseo-billing/components';

import type { ICardGiftCardForm } from './payment-methods.interfaceV2';
import type { IRsaResponse } from './rsa.interfaces';
import type { SavedCardReturnData } from './saved-cards.interface';
import type { IClubParis } from './summary.interface';

export interface PaymentMethod {
  methodId: string;
  sfccMethodId: string;
  enabled: boolean;
  name: string;
  title: string;
  subTitle?: string;
  processorId: string;
  canPayByInvoice?: boolean;
  canPayWithGiftCard?: boolean;
  canPayWithCencopay?: boolean;
  canPayMkpEasyProducts?: boolean;
  attributes: Attribute[];
  customerGroup: { [key: string]: string }[];
}

interface Attribute {
  label: Label;
  value: string;
}

enum Label {
  IsBicEnabled = 'isBicEnabled',
  IsSupportedBankListRequired = 'isSupportedBankListRequired',
  SOSPaymentMethodName = 'SOSPaymentMethodName'
}

export interface PaymentMethodResponseData {
  cencosudCardBINs?: string[];
  isSaga?: boolean;
  safeData?: IRsaResponse;
  paymentsInstruments: PaymentsInstrument[];
  availablePaymentsInstruments?: PaymentMethod[];
  savedCards?: SavedCardReturnData[];
  cencoFees: CencoFee[];
  creditFees: CreditFee[];
  clubParis?: IClubParis;
}
export interface CencoFee {
  fee: number;
}

export interface CreditFee {
  fee: number;
}

export interface PaymentsInstrument {
  id: string;
  title: string;
  subTitle?: string;
  isInvoiceCompatible?: boolean;
  diffPrice?: number | null;
  saveLabel?: string | null;
  totalPrices?: TotalPrice[] | null;
  enable?: boolean;
}

export interface TotalPrice {
  id: string;
  label: string;
  price: number;
  formattedPrice: string;
}

export interface ICardNumberForm {
  cardNumber: string;
}

export interface ICardRemoveGiftCard {
  paymentInstrumentId: string;
}

export interface IPaymentError {
  statusCode: number;
  error: string;
  title: string;
  message: string;
  code: number;
}

interface PaymentInfoSavedCard {
  paymentInstrumentId: string;
  cvv: string;
  installments: string;
  idKey: string;
}

export interface PaymentInfoCreditCard {
  cardNumber: string;
  cardExpirationDate: string;
  cvv: string;
  installments: number;
  savedCard: boolean;
  holder: string;
  idKey: string;
}

interface PaymentDetails {
  id: string;
}

export interface IPaymentMethodRequest {
  paymentDetails: PaymentDetails;
  paymentInfo?:
    | PaymentInfoCreditCard
    | ICardGiftCardForm
    | PaymentInfoSavedCard;
}

export interface IFeesSelectOptions {
  name: string;
  value: number;
}

export interface IExtendedPaymentMethodResponseData {
  isSaga?: boolean;
  safeData?: IRsaResponse;
  paymentsInstruments: IExtendedPaymentsInstrument[];
  savedCards?: SavedCardReturnData[];
  cencoFees: CencoFee[];
  creditFees: CreditFee[];
  clubParis?: IClubParis;
  cencosudCardBINs?: string[];
  reachMaxRetries?: number;
  blockingTime?: number;
}

export enum EComponentType {
  SAVED_CARD = 'SAVED_CARD',
  DIGITAL_WALLET = 'DIGITAL_WALLET',
  OTHER_PAYMENT_METHOD = 'OTHER_PAYMENT_METHOD',
  CLUB = 'CLUB'
}

export interface IExtendedPaymentsInstrument {
  title: string;
  paymentMethods: IExtendedPaymentMethod[];
  componentType: EComponentType;
}

export type TPaymentMethodTotalPrices = Array<{
  id: string;
  label: string;
  formattedPrice: string;
}>;

export interface IExtendedPaymentMethod {
  methodId: string;
  enabled: boolean;
  name: string;
  title: string;
  subTitle?: string;
  processorId: string;
  canPayByInvoice?: boolean;
  attributes: Attribute[];
  customerGroup: { [key: string]: string }[];
  applied?: boolean;
  id?: string;
  isInvoiceCompatible: boolean;
  isCencopayCompatible: boolean;
  isGiftCardCompatible: boolean;
  disableText?: string;
  diffPrice: number | null;
  saveLabel: string | null;
  componentType: EComponentType;
  paymentMethodId?: string;
  paymentInstrumentId?: string;
  cardType?: string | null;
  numberLastDigits?: string;
  isExpired?: boolean;
  bankName?: string;
  isCencosudCard?: boolean;
  isCreditCard?: boolean;
  selected?: boolean;
  sortView?: number;
  details?: null;
  feedBack?: IFeedBack[];
  tooltip?: {
    title: string;
    description: string;
  };
  totalPrices?: TPaymentMethodTotalPrices;
}

export interface IFeedBack {
  title: string;
  subtitle: string;
  type: keyof typeof FeedbackType;
}
